import { MemberStatus } from 'src/app/features/member/member'
import { Component, OnInit } from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'
import { RequestQueryBuilder } from '@nestjsx/crud-request'
import { Store } from '@ngrx/store'
import moment from 'moment-mini'
import { BsModalService } from 'ngx-bootstrap/modal'
import {
  BehaviorSubject,
  combineLatest,
  firstValueFrom,
  forkJoin,
  interval,
  Observable,
  of,
  Subscription as RxSubscription
} from 'rxjs'
import {
  catchError,
  filter,
  map,
  startWith,
  take,
  takeWhile
} from 'rxjs/operators'
import { authSelectors } from 'src/app/core/auth/auth.selectors'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'
import { environment } from 'src/environments/environment'
import { cartItemActions } from '../../cart/cart-item/cart-item.actions'
import type {
  CheckoutPaymentBrand,
  LegacyDirectDebitConfig
} from '../../configuration-option/configuration-option'
import { configurationOptionSelectors } from '../../configuration-option/configuration-option.selectors'
import { Member } from '../../member/member'
import {
  CollecStatusType,
  Payment,
  PaymentProviderType,
  PayMethodType,
  StoredPayMethodCapablePsps
} from '../payment/payment'
import { Sale, StatusCode } from '../sale'
import { SaleItem } from '../sale-item/sale-item'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import { operationActions } from '../../operation/operation.actions'
import { SaleService } from '../sale.service'
import { ConfigurationService } from '../../../shared/services/configuration.service'
import { ConfigOptionsKeys } from '../../../shared/constants'
import { productActions } from '../../product/product.actions'
import { BreadCrumbActions } from 'src/app/shared/components/breadcrumb/breadcrumb.actions'
import { AppRoutesNS } from 'src/app/app-routes.namespace'
import { Document } from '../../document/document'
import { StoredPayMethodService } from '../../stored-pay-method/stored-pay-method.service'
import {
  StoredPayMethod,
  StoredPayMethodType
} from '../../stored-pay-method/stored-pay-method'
import { Organization } from '../../organization/organization'
import { SaleItemService } from '../sale-item/sale-item.service'
import { normalizationActions } from 'src/app/shared/normalization/normalization.actions'
import { normalizeToSchema } from 'src/app/shared/normalization/normalization'
import { cartItemSelectors } from '../../cart/cart-item/cart-item.selectors'
import { SaleFailedModalComponent } from '../sale-failed-modal/sale-failed-modal.component'
import { TranslateService } from '@ngx-translate/core'
import { authActions } from 'src/app/core/auth/auth.actions'
import { bookingActions } from '../../booking/booking.actions'
import { participationActions } from '../../booking/participation/participation.actions'
import { CreateStoredPayMethodModalComponent } from './create-stored-paymethod-modal/create-stored-paymethod-modal.component'
import { AuthService } from 'src/app/core/auth/auth.service'
import { answerActions } from '../../survey/answer/answer.actions'
import { subscriberActions } from '../../subscription/subscriber/subscriber.actions'
import {
  excludeGiftCardAdjustments,
  hasInFlightPspPayment,
  shouldRequireStoredPayMethodSetup,
  shouldShowGiftCardSection
} from './gift-card-visibility.utils'

const defaultSaleExpirationSeconds = 2700 as const
const maxSalePollingCount = 100
const storedPayMethodSetupQueryParam = 'storedPayMethodSetup'
const giftCardStoredPayMethodSetupQueryValue = 'giftCard'
const brandIconsPath = 'assets/svg/brands'
const checkoutPaymentBrandIcons: {
  id: CheckoutPaymentBrand
  alt: string
  path: string
  width: number
}[] = [
  { id: 'visa', alt: 'Visa', path: `${brandIconsPath}/visa.svg`, width: 38 },
  {
    id: 'mastercard',
    alt: 'MasterCard',
    path: `${brandIconsPath}/mastercard.svg`,
    width: 25
  },
  {
    id: 'maestro',
    alt: 'Maestro',
    path: `${brandIconsPath}/maestro.svg`,
    width: 25
  },
  { id: 'sepa', alt: 'Sepa', path: `${brandIconsPath}/sepa.svg`, width: 43 },
  { id: 'ideal', alt: 'Ideal', path: `${brandIconsPath}/ideal.svg`, width: 22 },
  {
    id: 'apple-pay',
    alt: 'Apple Pay',
    path: `${brandIconsPath}/apple-pay.svg`,
    width: 38
  },
  {
    id: 'google-pay',
    alt: 'Google Pay',
    path: `${brandIconsPath}/google-pay.svg`,
    width: 38
  },
  {
    id: 'paypal',
    alt: 'PayPal',
    path: `${brandIconsPath}/paypal.svg`,
    width: 38
  },
  {
    id: 'bancontact',
    alt: 'Bancontact',
    path: `${brandIconsPath}/bancontact.svg`,
    width: 38
  }
]
const payMethodsAllowedWithLegacyDirectDebit: PayMethodType[] = [
  PayMethodType.PAY_METHOD_ACCOUNT,
  PayMethodType.PAY_METHOD_DIRECT_DEBIT
]
interface FetchResult {
  sale: Sale | null
  payments: Payment[]
  saleItems: SaleItem[]
  storedPayMethods: StoredPayMethod[]
}
@UntilDestroy()
@Component({
  selector: 'sale-checkout',
  templateUrl: './show.component.html',
  styleUrls: ['./show.component.scss']
})
export class SaleCheckoutShowComponent implements OnInit {
  public checkoutStage: 'unstarted' | 'inPayment' = 'unstarted'

  public paymentProvidersSettings = environment.paymentProviderSettings
  public selectedPaymentProviderType: PaymentProviderType | null =
    this.paymentProvidersSettings?.[0]?.name
  public selectedPaymentMethod: PayMethodType | null = null
  public selectedStoredPayMethodId: number | null = null
  public selectablePayMethods: PayMethodType[] = []
  public selectableStoredPayMethods: StoredPayMethod[] = []
  public paymentModuleConfig = environment.moduleConfigs.paymentModule
  public bookingModuleConfig = environment.moduleConfigs.bookingModule
  public shopModuleConfig = environment.moduleConfigs.shopModule
  public showDownloadTicketsButton = environment.showDownloadTicketsButton
  public activeTicketingBlockModule =
    environment.moduleConfigs?.shopModule?.shopIndex?.ticketingBlock ?? false
  public StatusCode = StatusCode
  public MemberStatus = MemberStatus
  public AppRoutesNS = AppRoutesNS
  public PaymentProviderType = PaymentProviderType
  public PayMethodType = PayMethodType
  public newPayMethodOrPayOnlineTranslationString: string =
    this.getNewPayMethodOrPayOnlineTranslationString()
  public member: Member
  public sale: Sale
  public payments: Payment[]
  public amountAlreadyPaid: number = 0
  public dueLaterAmount: number = 0
  public dueNowAmount: number = 0
  public isLoading: boolean = false
  public organization: Organization | null = null
  public saleDocuments: Document[] = []
  public checkoutPaymentBrandIcons$: Observable<
    typeof checkoutPaymentBrandIcons
  > = of([])
  public shouldCreateStoredPayMethod: boolean = false
  public userWantsToSaveStoredPayMethod: boolean = true
  public isStoredPayMethodCreationSideEffectOfPayment: boolean = false

  public showGiftCardSection: boolean = false
  public giftCardAttemptState: 'prestine' | 'invalid' | 'success' = 'prestine'
  public isApplyingGiftCard: boolean = false
  public giftCardReferenceCode: string
  public giftCardFormIsVisible: boolean = false
  public saleItems$: BehaviorSubject<SaleItem[]> = new BehaviorSubject([])

  public pendingSaleSecondsRemaining$: Observable<number>
  public pendingSaleHasExpired$: Observable<boolean>
  public salePollingSubscription: RxSubscription
  public saleDocumentsPollingSubscription: RxSubscription
  public storedPayMethodSetupPollingSubscription: RxSubscription
  public prePaymentFactor$: Observable<number>

  private legacyDmsDirectDebitActive: boolean = false
  private legacyDmsDirectDebitRequiresFirstPaymentOnline: boolean = false
  public legacyStoredDirectDebitStoredPayMethod: StoredPayMethod | null = null
  public futurePaymentsBilledByDirectDebit: boolean = false

  constructor(
    private store: Store<AppState>,
    private router: Router,
    private route: ActivatedRoute,
    private notificationService: NotificationService,
    private modalService: BsModalService,
    public saleService: SaleService,
    public saleItemService: SaleItemService,
    public configurationService: ConfigurationService,
    private configService: ConfigurationService,
    private storedPayMethodService: StoredPayMethodService,
    private translate: TranslateService,
    private authService: AuthService
  ) {}

  async ngOnInit() {
    this.store.dispatch(
      BreadCrumbActions.addPageToHistory({
        pageName: 'saleModule.checkout',
        url: AppRoutesNS.Cart.CART,
        showBackArrow: false,
        titleName: 'saleModule.checkout'
      })
    )

    this.member = await firstValueFrom(
      this.store
        .select(authSelectors.selectMember)
        .pipe(filter((member) => Boolean(member)))
    )

    this.store.dispatch(operationActions.fetchMyOperations({}))

    this.checkoutPaymentBrandIcons$ = this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType(
          ConfigOptionsKeys.CHECKOUT_PAYMENT_BRANDS
        )
      )
      .pipe(map((brandIds) => this.getCheckoutPaymentBrandIcons(brandIds)))

    const saleIdString = this.route.snapshot.paramMap.get('saleId')

    const saleId = Number(saleIdString)

    this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType(
          ConfigOptionsKeys.LEGACY_DIRECT_DEBIT_CONFIG
        )
      )
      .pipe(
        filter((c) => Boolean(c)),
        untilDestroyed(this),
        take(1)
      )
      .subscribe((legacyDirectDebitConfig) => {
        this.legacyDmsDirectDebitActive = Boolean(legacyDirectDebitConfig)
        this.legacyDmsDirectDebitRequiresFirstPaymentOnline =
          this.getLegacyDirectDebitRequiresFirstPaymentOnline(
            legacyDirectDebitConfig
          )
      })

    await this.saleFetchAndSideEffects(saleId, this.member, true)

    if (
      this.route.snapshot.queryParamMap.get(storedPayMethodSetupQueryParam) ===
      giftCardStoredPayMethodSetupQueryValue
    ) {
      if (this.giftCardRequiresStoredPayMethodSetup()) {
        this.startStoredPayMethodSetupPolling(saleId)
      } else {
        this.clearStoredPayMethodSetupQueryParam()
      }
    }

    this.store
      .select(
        configurationOptionSelectors.selectByKeyWithType(
          ConfigOptionsKeys.ONLINE_PAYMENTS_POS_PRODUCT_ID
        )
      )
      .pipe(
        filter((c) => Boolean(c)),
        untilDestroyed(this),
        take(1)
      )
      .subscribe((posProductId) => {
        this.store.dispatch(
          productActions.fetchOneById({
            id: Number(posProductId)
          })
        )
      })

    this.prePaymentFactor$ = this.configService
      .selectConfigValueById$(ConfigOptionsKeys.PRE_PAYMENT_FACTOR)
      // starts with 0 to avoid waiting for the first value
      .pipe(
        startWith({ value: 0 }),
        map(({ value }) => Number(value))
      )

    this.pendingSaleSecondsRemaining$ = interval(1000).pipe(
      untilDestroyed(this),
      takeWhile((_i) => [StatusCode.INFLIGHT].includes(this.sale.statusCode)),
      map((_i) => this.getSaleExpiresInSeconds(this.sale, this.payments))
    )

    this.pendingSaleHasExpired$ = this.pendingSaleSecondsRemaining$.pipe(
      map((secondsRemaining) => secondsRemaining <= 0),
      startWith(false)
    )
  }

  // this is the primany function that is called on init and in polling that fetches sale and sets things like allowed paymethods, stored paymethods etc
  private async saleFetchAndSideEffects(
    saleId: number,
    member: Member,
    isInitialFetch: boolean = false
  ) {
    this.isLoading = true
    const { sale, payments, saleItems, storedPayMethods } =
      await this.fetchSaleAndRelatedData(saleId, member)

    if (!sale?.id) {
      console.error('Failed to fetch sale')
      return
    }

    // start maintain saleItems for downsteam components for now until further refactoring
    this.saleItems$.next(saleItems)
    this.store.dispatch(
      normalizationActions.upsertMany({
        entities: normalizeToSchema({ saleItems: saleItems, sales: [sale] })
      })
    )
    // end maintain saleItems for downsteam components for now until further refactoring

    this.onSaleUpdate(sale, payments, isInitialFetch)

    const allowedPayMethodsConfigOptValue = await firstValueFrom(
      this.store
        .select(
          configurationOptionSelectors.selectByKeyWithType(
            ConfigOptionsKeys.ALLOWED_PAY_METHODS
          )
        )
        .pipe(take(1))
    )
    const mixPaymentConfigOptValue = await firstValueFrom(
      this.store
        .select(
          configurationOptionSelectors.selectByKeyWithType(
            ConfigOptionsKeys.MIX_PAYMENT_METHODS
          )
        )
        .pipe(take(1))
    )

    const legacyDirectDebitConfigOptValue = await firstValueFrom(
      this.store
        .select(
          configurationOptionSelectors.selectByKeyWithType(
            ConfigOptionsKeys.LEGACY_DIRECT_DEBIT_CONFIG
          )
        )
        .pipe(take(1))
    )
    this.legacyDmsDirectDebitActive =
      this.legacyDmsDirectDebitActive ||
      Boolean(legacyDirectDebitConfigOptValue)
    this.legacyDmsDirectDebitRequiresFirstPaymentOnline =
      this.getLegacyDirectDebitRequiresFirstPaymentOnline(
        legacyDirectDebitConfigOptValue
      )

    const allowedPayMethods = await this.getAllowedPayMethods(
      saleItems,
      allowedPayMethodsConfigOptValue,
      mixPaymentConfigOptValue,
      sale.statusCode === StatusCode.PENDING_REQUIRED
    )

    const allowsLegacyDirectDebit =
      this.payMethodsAlignedToLegacyDirectDebitSetup(allowedPayMethods)

    const legacyDirectDebitOnlyCheckout =
      this.legacyDmsDirectDebitActive && allowsLegacyDirectDebit
    const directDebitOnlyCheckout =
      allowedPayMethods?.length === 1 &&
      allowedPayMethods.includes(PayMethodType.PAY_METHOD_DIRECT_DEBIT)
    const checkoutAllowedPayMethods =
      legacyDirectDebitOnlyCheckout &&
      this.legacyDmsDirectDebitRequiresFirstPaymentOnline
        ? [...allowedPayMethods, PayMethodType.PAY_METHOD_CREDIT_CARD]
        : allowedPayMethods

    // this controls the main difference in stored paymethod creation flow between psps
    // some do it as side effect of a sale, like mollie, where the rest like worldline and legacy direct debit etc
    // force user to create a stored payemthod before doing the sale checkout

    this.isStoredPayMethodCreationSideEffectOfPayment =
      !legacyDirectDebitOnlyCheckout &&
      StoredPayMethodCapablePsps.includes(this.selectedPaymentProviderType)
    if (sale.statusCode !== StatusCode.PENDING_REQUIRED) {
      this.shouldCreateStoredPayMethod =
        this.getShouldCreateStoredPayMethod(
          storedPayMethods,
          saleItems,
          payments
        ) ||
        (directDebitOnlyCheckout && !storedPayMethods.length) // hotfix for edge cases not taken into account
    }

    this.selectablePayMethods = this.getSelectablePayMethods(
      checkoutAllowedPayMethods,
      member,
      storedPayMethods,
      payments
    )
    this.legacyStoredDirectDebitStoredPayMethod = null
    this.selectableStoredPayMethods = this.getSelectableStoredPayMethods(
      storedPayMethods,
      allowedPayMethods
    )
    this.futurePaymentsBilledByDirectDebit =
      this.getFuturePaymentsBilledByDirectDebit(saleItems)

    this.organization = sale.organization?.id ? sale.organization : null

    this.showGiftCardSection = shouldShowGiftCardSection({
      showGiftCard: this.paymentModuleConfig.showGiftCard,
      shouldCreateStoredPayMethod: this.shouldCreateStoredPayMethod,
      isStoredPayMethodCreationSideEffectOfPayment:
        this.isStoredPayMethodCreationSideEffectOfPayment,
      allowGiftCardBeforeStoredPayMethod:
        this.paymentModuleConfig.allowGiftCardBeforeStoredPayMethod,
      hasInFlightPspPayment: hasInFlightPspPayment(payments)
    })

    console.log(
      `Reached allowedPayMethods ${JSON.stringify(allowedPayMethods)} resulting in checkoutAllowedPayMethods ${JSON.stringify(checkoutAllowedPayMethods)} selectable pay method ${JSON.stringify(this.selectablePayMethods)} and selectableStoredPayMethods ${JSON.stringify(this.selectablePayMethods)} and shouldCreateStoredPayMethod ${this.shouldCreateStoredPayMethod}`
    )

    // handle paymethod / stored paymethod preselection
    if (
      this.shouldCreateStoredPayMethod &&
      !this.isStoredPayMethodCreationSideEffectOfPayment
    ) {
      console.log('Should create stored paymthod before doing checkout')
    } else if (
      this.canUseLegacyDirectDebitForCheckout(
        allowedPayMethods,
        storedPayMethods
      )
    ) {
      console.log(
        'Legacy direct debit stored paymethod will be used for checkout'
      )
      this.onSelectLegacyDirectDebitStoredPayMethod()
    } else if (!isInitialFetch) {
      console.log(
        'Is not initial load and wont preselect stored paymethod or paymethod'
      )
    } else if (this.selectableStoredPayMethods.length > 0) {
      const validStoredPayMethods = this.selectableStoredPayMethods.filter(
        (s) => !this.isStoredPayMethodExpired(s)
      )

      const preferredStoredPayMethod = validStoredPayMethods?.find(
        (s) => s.preferred
      )
      console.log(
        'Selectable stored paymethods',
        this.selectableStoredPayMethods,
        'and validStoredPayMethods',
        validStoredPayMethods,
        'and perferred stored paymethod',
        preferredStoredPayMethod
      )
      this.onSelectStoredPayMethod(
        preferredStoredPayMethod ?? validStoredPayMethods[0]
      )
    } else {
      console.log('Has selectable paymethods', this.selectablePayMethods)
      this.onSelectPaymentMethod(this.selectablePayMethods[0])
    }

    this.isLoading = false
  }

  public async onClickPay(sale: Sale) {
    if (!this.validatePaymentMethod()) {
      return
    }

    if (
      this.shouldCreateStoredPayMethod &&
      !this.isStoredPayMethodCreationSideEffectOfPayment
    ) {
      this.notificationService.emit(
        'paymentModule.mustCreateStoredPayMethodSubtext',
        'warning'
      )
      return
    }

    this.checkoutStage = 'inPayment'

    // check if there is already a hosted page
    const hostedPages = this.saleService.getHostedPagesForSaleFromLocalStorage(
      sale.id,
      Number(this.selectedPaymentProviderType)
    )

    const targetHostedPage = hostedPages?.find((hp) => {
      const diffInMinutes = moment().diff(moment(hp.date), 'minutes')
      if (diffInMinutes < 5) {
        return true
      }
    })

    if (
      targetHostedPage &&
      [PayMethodType.PAY_METHOD_CREDIT_CARD].includes(
        this.selectedPaymentMethod
      )
    ) {
      console.log(
        `Found hosted page in local storage that is still valid and will redirect to it: ${targetHostedPage.hostedPaymentPageUrl}`
      )
      window.location.href = targetHostedPage.hostedPaymentPageUrl
      return false
    }

    try {
      const salePayResult = await firstValueFrom(
        this.saleService.salePay(
          sale.id,
          this.selectedPaymentMethod,
          this.selectedStoredPayMethodId,
          window.location.origin + '/sales/callback',
          this.userWantsToSaveStoredPayMethod,
          this.selectedPaymentProviderType
            ? Number(this.selectedPaymentProviderType)
            : null
        )
      )

      console.log('Received response from salePay', salePayResult)

      // clear previous hosted pages
      this.saleService.resetHostedPagesLocalStorage()

      if (!salePayResult) {
        console.error('Payment adaptor call failed without response')
        this.notificationService.emit('paymentModule.paymentFailed', 'error')
        return
      }

      if (salePayResult.userInitiatedPaymentResult) {
        if (!salePayResult.userInitiatedPaymentResult.bSuccess) {
          console.error(
            'Payment adaptor call was not successful',
            salePayResult
          )
          this.notificationService.emit(
            'paymentModule.paymentFailed ' +
              salePayResult.userInitiatedPaymentResult.message,
            'error'
          )
          this.checkoutStage = 'unstarted'
          this.isLoading = false
          return
        }
        if (salePayResult.userInitiatedPaymentResult.bPaidOnStoredPayMethod) {
          console.log(
            `Get inststant success, sale could very well to fully done with this single payment, will redirect to callback with success status`
          )
          this.router.navigate([AppRoutesNS.Sales.SALES_CALLBACK], {
            queryParams: {
              status: 'success',
              paymentReference: salePayResult.createdPayment.reference
            }
          })
        } else if (
          salePayResult.userInitiatedPaymentResult.hostedPaymentPageUrl
        ) {
          // set hosted page url in local storage for reuse on user refreshes
          this.saleService.setHostedPagesInLocalStorage(
            salePayResult.sale.id,
            salePayResult.createdPayment.id,
            salePayResult.userInitiatedPaymentResult.hostedPaymentPageUrl,
            salePayResult.createdPayment.paymentProviderType
          )

          const isMobileApp = await firstValueFrom(
            this.configurationService.isMobileApp$
          )
          if (isMobileApp) {
            this.startSaleStatusPolling(salePayResult.sale.id, this.member)
          }
          console.log(
            `Will redirect to external url in sale checkout: ${salePayResult.userInitiatedPaymentResult.hostedPaymentPageUrl}`
          )
          window.location.href =
            salePayResult.userInitiatedPaymentResult?.hostedPaymentPageUrl
        }
      } else {
        // invoice and balance payments end up here as there are not calling userInitiatedPayment flow in api to call psps
        this.notificationService.emit('paymentModule.paymentSuccess', 'success')
        this.member = await firstValueFrom(this.authService.getMember())
        this.onSaleUpdate(salePayResult.sale, salePayResult.sale.payments)
        if (salePayResult.sale.statusCode !== StatusCode.DONE) {
          this.saleFetchAndSideEffects(sale.id, this.member, true)
        }
      }
    } catch (error) {
      console.log('Received error from salePay', error)
      this.checkoutStage = 'unstarted'
    }
  }

  public onSelectPaymentMethod(payMethod: PayMethodType) {
    this.selectedStoredPayMethodId = null
    this.selectedPaymentMethod = payMethod
  }

  public isPaymentMethodSelected(payMethod: PayMethodType): boolean {
    return (
      this.selectedStoredPayMethodId == null &&
      this.selectedPaymentMethod === payMethod
    )
  }

  public isPaymentMethodSelectable(payMethod: PayMethodType): boolean {
    return Boolean(this.selectablePayMethods?.find((x) => x === payMethod))
  }

  public onSelectStoredPayMethod(storedPayMethod: StoredPayMethod) {
    if (!storedPayMethod?.id) {
      return
    }

    if (this.storedPayMethodService.isExpired(storedPayMethod)) {
      return
    }

    this.selectedStoredPayMethodId = storedPayMethod.id
    this.selectedPaymentProviderType = Number(
      storedPayMethod.paymentProviderType
    )

    this.selectedPaymentMethod =
      storedPayMethod.type == StoredPayMethodType.CARD_ON_FILE
        ? PayMethodType.PAY_METHOD_CREDIT_CARD
        : storedPayMethod.type == StoredPayMethodType.PAYPAL
          ? PayMethodType.PAY_METHOD_CREDIT_CARD
          : PayMethodType.PAY_METHOD_DIRECT_DEBIT
  }

  private onSelectLegacyDirectDebitStoredPayMethod(): void {
    if (!this.legacyStoredDirectDebitStoredPayMethod?.id) {
      return
    }

    this.selectedStoredPayMethodId =
      this.legacyStoredDirectDebitStoredPayMethod.id
    this.selectedPaymentProviderType = null
    this.selectedPaymentMethod = PayMethodType.PAY_METHOD_DIRECT_DEBIT
  }

  // this does the stored payemthod creation for all psps that dont create a stored pay method as side effect of doing the payment
  // examples are legacy direct debit, worldline
  public async onClickCreateStoredPayMethod(
    storedPayMethodTypeOverride: StoredPayMethodType = StoredPayMethodType.DIRECT_DEBIT
  ) {
    // if legacy direct debit is active, call the create stored paymethod legacy direct debit modal
    if (this.legacyDmsDirectDebitActive) {
      return this.createLegacyDirectDebitStoredPayMethod()
    }

    try {
      this.isLoading = true

      const returnUrl = new URL(
        `/sales/${this.sale.id}/checkout`,
        window.location.origin
      )
      if (storedPayMethodTypeOverride === StoredPayMethodType.CARD_ON_FILE) {
        returnUrl.searchParams.set(
          storedPayMethodSetupQueryParam,
          giftCardStoredPayMethodSetupQueryValue
        )
      }

      const createStoredPayMethodResult = await firstValueFrom(
        this.storedPayMethodService.create(
          returnUrl.href,
          null, // we dont care about paymethod to be created atm
          storedPayMethodTypeOverride,
          this.selectedPaymentProviderType
            ? Number(this.selectedPaymentProviderType)
            : null
        )
      )

      if (!createStoredPayMethodResult.bSuccess) {
        console.error(
          'failed to create stored paymethod',
          createStoredPayMethodResult.message
        )
        this.notificationService.emit(
          createStoredPayMethodResult.message,
          'warning'
        )
        this.isLoading = false
      } else if (createStoredPayMethodResult.hostedPaymentPageUrl) {
        console.log(
          `Created stored paymethod which require a redirect to a hosted page and will redirect there ${createStoredPayMethodResult.hostedPaymentPageUrl}`
        )
        window.location.href = createStoredPayMethodResult.hostedPaymentPageUrl
      } else {
        console.log(
          'Created stored paymethod which didnt require a redirect to a hosted page'
        )

        await this.saleFetchAndSideEffects(this.sale.id, this.member)
      }
    } catch (error) {
      console.error('failed to create stored paymethod', error)
      this.isLoading = false
    }
  }

  public onClickCreateGiftCardStoredPayMethod() {
    return this.onClickCreateStoredPayMethod(StoredPayMethodType.CARD_ON_FILE)
  }

  public giftCardRequiresStoredPayMethodSetup(): boolean {
    return shouldRequireStoredPayMethodSetup({
      allowGiftCardBeforeStoredPayMethod:
        this.paymentModuleConfig.allowGiftCardBeforeStoredPayMethod,
      shouldCreateStoredPayMethod: this.shouldCreateStoredPayMethod,
      isStoredPayMethodCreationSideEffectOfPayment:
        this.isStoredPayMethodCreationSideEffectOfPayment
    })
  }

  public onClickDownloadTicket(saleDocuments: Document[]) {
    if (!saleDocuments?.length) {
      return
    }

    for (const document of saleDocuments) {
      if (document?.publicUrl?.length) {
        window.open(document?.publicUrl, '_blank').focus()
      }
    }
  }

  public async onClickCancel() {
    if (this.sale.statusCode === StatusCode.PENDING_REQUIRED) {
      this.notificationService.emit(
        'saleModule.cantCancelRequiredSale',
        'warning'
      )
      return
    }

    if (!confirm(this.translate.instant('saleModule.cancelSalesResource'))) {
      return
    }

    this.isLoading = true
    try {
      const cancelledSale = await firstValueFrom(
        this.saleService.updateOne(this.sale.id, {
          statusCode: StatusCode.CANCELLED
        })
      )

      this.onCancelSideEffects(cancelledSale)
      this.notificationService.emit('alerts.success', 'success')
    } catch (error) {
      this.isLoading = false
      console.log('Failed to cancel sale')
    }
  }

  public async attemptGiftcard() {
    if (this.isLoading || this.isApplyingGiftCard || !this.giftCardReferenceCode) {
      return
    }
    this.isApplyingGiftCard = true
    this.isLoading = true

    try {
      await firstValueFrom(
        this.saleItemService.createOne(
          Object.assign(new SaleItem(), {
            siteId: this.sale.siteId,
            saleId: this.sale.id,
            params: {
              giftCardReferenceCode: this.giftCardReferenceCode
            }
          })
        )
      )

      this.giftCardAttemptState = 'success'
      await this.saleFetchAndSideEffects(this.sale.id, this.member)
    } catch (error) {
      console.error('Failed to apply giftcard', error)
      this.isLoading = false
      this.giftCardAttemptState = 'invalid'
    } finally {
      this.isApplyingGiftCard = false
    }
  }
  // mark dirty af
  public getPrepaymentFactor(factor: number): number {
    return Number((factor * 100).toFixed(2))
  }

  public getLandingPageRedirect(): string {
    return environment.moduleConfigs.authModule.afterLoginSuccessRedirect
  }

  public isStoredPayMethodExpired(
    storedPayMethod: StoredPayMethod | null
  ): boolean {
    return this.storedPayMethodService.isExpired(storedPayMethod)
  }

  public getStoredPayMethodIcon(storedPayMethod: StoredPayMethod): string {
    if (
      [
        StoredPayMethodType.DIRECT_DEBIT,
        StoredPayMethodType.LEGACY_DIRECT_DEBIT
      ].includes(storedPayMethod.type)
    ) {
      return `${brandIconsPath}/sepa.svg`
    }
    if (storedPayMethod.type === StoredPayMethodType.PAYPAL) {
      return `${brandIconsPath}/paypal.svg`
    }
    if (
      ['mastercard', 'mc'].some((keyword) =>
        storedPayMethod.description
          ?.toLowerCase()
          .includes(keyword.toLowerCase())
      )
    ) {
      return `${brandIconsPath}/mastercard.svg`
    }
    if (
      ['maestro'].some((keyword) =>
        storedPayMethod.description
          ?.toLowerCase()
          .includes(keyword.toLowerCase())
      )
    ) {
      return `${brandIconsPath}/maestro.svg`
    }
    if (
      ['visa'].some((keyword) =>
        storedPayMethod.description
          ?.toLowerCase()
          .includes(keyword.toLowerCase())
      )
    ) {
      return `${brandIconsPath}/visa.svg`
    }
    if (
      ['apple'].some((keyword) =>
        storedPayMethod.description
          ?.toLowerCase()
          .includes(keyword.toLowerCase())
      )
    ) {
      return `${brandIconsPath}/apple-pay.svg`
    }
    if (
      ['google'].some((keyword) =>
        storedPayMethod.description
          ?.toLowerCase()
          .includes(keyword.toLowerCase())
      )
    ) {
      return `${brandIconsPath}/google-pay.svg`
    }

    if (storedPayMethod.description?.toLowerCase().includes('paypal')) {
      return `${brandIconsPath}/paypal.svg`
    }
    if (storedPayMethod.type === StoredPayMethodType.CARD_ON_FILE) {
      return `${brandIconsPath}/maestro.svg`
    }

    return `${brandIconsPath}/sepa.svg`
  }

  private getCheckoutPaymentBrandIcons(
    brandIds: CheckoutPaymentBrand[] | null
  ): typeof checkoutPaymentBrandIcons {
    const visibleBrandIds = Array.isArray(brandIds) ? brandIds : []

    return checkoutPaymentBrandIcons.filter((brandIcon) =>
      visibleBrandIds.includes(brandIcon.id)
    )
  }

  // this function is really only usable in delcom dev envs and e2e as normally
  // customers have just 1 payment service provider so there is no changing it via the switcher in cf
  // this function will just recall the core saleFetchAndSideEffects so simulate the most realistically
  // how it would be for a normal user entering the checkout
  public async onSetSelectedPaymentProviderType(event: Event) {
    const selectElement = event.target as HTMLSelectElement
    if (!selectElement.value) {
      return
    }

    this.selectedPaymentProviderType = parseInt(selectElement.value)
    this.selectedStoredPayMethodId = null

    this.newPayMethodOrPayOnlineTranslationString =
      this.getNewPayMethodOrPayOnlineTranslationString()

    console.log(this.newPayMethodOrPayOnlineTranslationString)
    await this.saleFetchAndSideEffects(this.sale.id, this.member)
  }

  public balanceWouldBePartialPayment(): boolean {
    if (!this.member?.balance) {
      return false
    }

    if (!this.dueNowAmount) {
      return false
    }
    if (Number(this.member.balance) < Number(this.dueNowAmount)) {
      return true
    }
  }

  // this is the main and only fetch function for all the primary requests we need to run this page
  private async fetchSaleAndRelatedData(
    saleId: number,
    member: Member
  ): Promise<FetchResult> {
    if (!(Number(saleId) > 0)) {
      console.error('Invalid saleId', saleId)
      return {
        sale: null,
        payments: [],
        saleItems: [],
        storedPayMethods: []
      }
    }
    return await firstValueFrom(
      forkJoin({
        sale: this.saleService
          .getOne(
            saleId,
            RequestQueryBuilder.create({
              search: {
                id: saleId,
                customerPersonId: member.id
              },
              join: [
                { field: 'organization' },
                { field: 'payments' },
                { field: 'documents' },
                { field: 'creditSales' }
              ]
            })
          )
          .pipe(take(1)),
        saleItems: this.saleItemService
          .getManyBySaleId(
            saleId,
            RequestQueryBuilder.create({
              join: [
                { field: 'product' },
                { field: 'product.location' },
                { field: 'product.translations' },
                { field: 'product.linkedSubscriptions' },
                { field: 'subscription' },
                { field: 'subscription.translations' },
                { field: 'subscription.linkedSubscriptions' },
                { field: 'course' },
                { field: 'course.translations' },
                { field: 'rental' }
              ]
            })
          )
          .pipe(take(1)),
        storedPayMethods: this.storedPayMethodService
          .getMany(
            RequestQueryBuilder.create({
              search: {
                personId: member.id
              }
            })
          )
          .pipe(
            take(1),
            map((response) => response.data as StoredPayMethod[])
          )
      }).pipe(
        map(({ sale, saleItems, storedPayMethods }) => ({
          sale,
          payments: sale?.payments ?? [],
          saleItems,
          storedPayMethods
        })),
        catchError((error) => {
          console.error('Failed to fetch data', error)
          this.notificationService.emit('saleModule.failedToLoadSale', 'error')
          return of({
            sale: null,
            payments: [],
            saleItems: [],
            storedPayMethods: []
          })
        })
      )
    )
  }

  private getSelectablePayMethods(
    allowedPayMethods: PayMethodType[],
    member: Member,
    storedPayMethods: StoredPayMethod[],
    payments: Payment[]
  ): PayMethodType[] {
    return allowedPayMethods.filter((payMethod) => {
      switch (payMethod) {
        case PayMethodType.PAY_METHOD_ACCOUNT: {
          if (!environment.moduleConfigs.paymentModule.showBalance) {
            return false
          }

          if (this.shouldCreateStoredPayMethod) {
            return false
          }

          if (member.balance) {
            const balance = parseFloat(member.balance.toString())
            return balance > 0
          }
          return false
        }
        case PayMethodType.PAY_METHOD_INVOICE: {
          if (this.shouldCreateStoredPayMethod) {
            return false
          }

          if (member.status !== MemberStatus.GUEST) {
            return true
          }
        }
        case PayMethodType.PAY_METHOD_CREDIT_CARD: {
          const isLegacyDirectDebitFirstPaymentOnline =
            this.legacyDmsDirectDebitActive &&
            this.legacyDmsDirectDebitRequiresFirstPaymentOnline &&
            allowedPayMethods.includes(PayMethodType.PAY_METHOD_DIRECT_DEBIT)

          if (
            isLegacyDirectDebitFirstPaymentOnline &&
            !this.hasLegacyDirectDebitStoredPayMethod(storedPayMethods)
          ) {
            return false
          }

          const hasFuturePayments = payments.some(
            (payment) =>
              !!payment?.dueDate && moment(payment.dueDate).isAfter(moment())
          )

          if (
            hasFuturePayments &&
            !storedPayMethods?.length &&
            !this.isStoredPayMethodCreationSideEffectOfPayment
          ) {
            return false
          }

          return true
        }
        case PayMethodType.PAY_METHOD_DIRECT_DEBIT: {
          // users must selected the stored paymethod in which they put their direct debit, this is no longer a selectable option
          return false
        }
      }

      return false
    })
  }

  private getShouldCreateStoredPayMethod(
    storedPayMethods: StoredPayMethod[],
    saleItems: SaleItem[],
    payments: Payment[]
  ): boolean {
    // only require a stored pay method if there is a due later payment config OR a subscription that auto renews

    let subscriptionRequiresStoredPayMethod = false

    for (const saleItem of saleItems) {
      if (saleItem.subscription) {
        if (saleItem.subscription.isAutoRenew) {
          subscriptionRequiresStoredPayMethod = true
          break
        }

        const paidTillDate = saleItem.params?.paidTillDate
          ? moment(saleItem.params?.paidTillDate)
          : null
        const endDate = saleItem.params?.endDate
          ? moment(saleItem.params?.endDate)
          : null

        if (paidTillDate?.isValid() && endDate?.isValid()) {
          if (paidTillDate.isSameOrBefore(endDate)) {
            subscriptionRequiresStoredPayMethod = true
            break
          }
        }
      }
    }

    const hasDueLaterPayments = payments?.filter(
      (p) => p.dueDate && moment(p.dueDate).isAfter(moment().startOf('day'))
    )
    const shouldHaveStoredMethod =
      hasDueLaterPayments?.length || subscriptionRequiresStoredPayMethod

    return Boolean(shouldHaveStoredMethod) && !storedPayMethods.length
  }

  private hasLegacyDirectDebitStoredPayMethod(
    storedPayMethods: StoredPayMethod[]
  ) {
    return storedPayMethods?.some(
      (storedPayMethod) =>
        storedPayMethod.type === StoredPayMethodType.LEGACY_DIRECT_DEBIT ||
        storedPayMethod.paymentProviderType ===
          PaymentProviderType.LegacyDmsDirectDebitBank
    )
  }

  private getFuturePaymentsBilledByDirectDebit(saleItems: SaleItem[]): boolean {
    return saleItems.some(
      (saleItem) =>
        saleItem.subscription?.payMethod ===
        PayMethodType.PAY_METHOD_DIRECT_DEBIT
    )
  }

  private getSelectableStoredPayMethods(
    storedPayMethods: StoredPayMethod[],
    allowedPayMethods: PayMethodType[]
  ): StoredPayMethod[] {
    return storedPayMethods.filter((storedPayMethod) => {
      if (
        storedPayMethod.type === StoredPayMethodType.DIRECT_DEBIT &&
        !allowedPayMethods?.includes(PayMethodType.PAY_METHOD_DIRECT_DEBIT)
      ) {
        // hide SEPA direct debit mandates when the cart's items do not allow
        // paying by direct debit, otherwise selecting one results in a 400 on pay.
        // PAY_METHOD_DIRECT_DEBIT is 0, so we must use includes() here and not the
        // truthiness of Array.find(), which would return the falsy 0 when matched.
        return false
      }

      if (
        storedPayMethod.paymentProviderType ===
        PaymentProviderType.LegacyDmsDirectDebitBank
      ) {
        // users in cf cant never use their stored paymethods of this type as legacy dms direct debit stored paymethods are only used the dms to create direct debit runs
        this.legacyStoredDirectDebitStoredPayMethod = storedPayMethod
        return false
      }

      if (
        this.legacyDmsDirectDebitActive &&
        storedPayMethod.type == StoredPayMethodType.LEGACY_DIRECT_DEBIT
      ) {
        // in legacy dms direct setup stored paymethods are not be selectable at all as legacy direct debit stored paymethods are just for dms to collect money with via the camt54 bank xmls
        // users cant use these direct debit mandate stored paymthods to complete any sale
        this.legacyStoredDirectDebitStoredPayMethod = storedPayMethod
        return false
      }

      if (storedPayMethod.paymentProviderType == PaymentProviderType.Nets) {
        // fitnessroom nets uses stored payethods for billing merchant initiated payments for subscriptions, but not for paying sales with, altough nexi the psp could do this
        return false
      }

      return true
    })
  }

  private getLegacyDirectDebitRequiresFirstPaymentOnline(
    legacyDirectDebitConfig: LegacyDirectDebitConfig | boolean | null
  ): boolean {
    return (
      Boolean(legacyDirectDebitConfig) &&
      typeof legacyDirectDebitConfig === 'object' &&
      !Array.isArray(legacyDirectDebitConfig) &&
      legacyDirectDebitConfig.requireFirstPaymentOnline === true
    )
  }
  private payMethodsAlignedToLegacyDirectDebitSetup = (
    payMethods: PayMethodType[]
  ): boolean => {
    if (!payMethods.includes(PayMethodType.PAY_METHOD_DIRECT_DEBIT)) {
      return false
    }

    if (
      payMethods.some(
        (payMethod) =>
          !payMethodsAllowedWithLegacyDirectDebit.includes(payMethod)
      )
    ) {
      return false
    }
    return true
  }

  private canUseLegacyDirectDebitForCheckout(
    allowedPayMethods: PayMethodType[],
    storedPayMethods: StoredPayMethod[]
  ): boolean {
    const hasStoredLegacyPayMethod =
      this.hasLegacyDirectDebitStoredPayMethod(storedPayMethods)
    if (
      this.legacyDmsDirectDebitRequiresFirstPaymentOnline &&
      hasStoredLegacyPayMethod
    ) {
      return false
    }

    if (!this.legacyStoredDirectDebitStoredPayMethod?.id) {
      return false
    }

    if (!this.payMethodsAlignedToLegacyDirectDebitSetup(allowedPayMethods)) {
      return false
    }

    return true
  }

  /**
   * Determines the set of payment methods that are valid for all sale items.
   *
   * Rule:
   * - Each sale item (product, subscription, or course.product) can define its own allowedPayMethods.
   * - If a sale item has no allowedPayMethods, fall back to the global active_allowed_pay_methods from configuration.
   * - The returned list is the intersection of payment methods across all sale items
   *   (i.e., only methods available for every item in the sale).
   * - If only one payment method remains, it is auto-selected.
   * - Pending required sales never allow using direct debit as a payment method.
   */
  private async getAllowedPayMethods(
    saleItems: SaleItem[],
    defaultAllowedPayMethod: PayMethodType[] | null,
    canMixPaymentMethods: boolean | null,
    isSalePendingRequired: boolean = false
  ) {
    // Ensures the given value is a valid array of payment methods
    const normalizePayMethods = (
      methods?: PayMethodType[] | null
    ): PayMethodType[] => (Array.isArray(methods) ? methods : [])

    // Returns the product's allowedPayMethods, or the global ones if empty
    const getEffectivePayMethods = (
      methods?: PayMethodType[]
    ): PayMethodType[] => {
      const payMethods = normalizePayMethods(methods)
      return payMethods.length ? payMethods : defaultAllowedPayMethod
    }

    // Will hold the common set of payment methods across all sale items
    let availablePaymentMethods: PayMethodType[] = []

    const allPaymentMethods = new Set<PayMethodType>()
    for (const item of excludeGiftCardAdjustments(saleItems)) {
      let itemPayMethods: PayMethodType[] = []

      // Retrieve payment methods for this sale item based on its type
      if (item.productId && item.product) {
        itemPayMethods = getEffectivePayMethods(item.product.allowedPayMethods)
      } else if (item.subscriptionId && item.subscription) {
        itemPayMethods = getEffectivePayMethods(
          item.subscription.allowedPayMethods
        )
      } else if (item.courseId && item.course && item.course.product) {
        itemPayMethods = getEffectivePayMethods(
          item.course.product.allowedPayMethods
        )
      }

      // Group all pay methods of all sale items
      itemPayMethods.forEach((pm) => allPaymentMethods.add(pm))

      if (!availablePaymentMethods.length) {
        // First item sets the starting point
        availablePaymentMethods = itemPayMethods.filter((pm) =>
          defaultAllowedPayMethod?.includes(pm)
        )
      } else {
        // Keep only the payment methods that are common so far
        availablePaymentMethods = availablePaymentMethods.filter(
          (pm) =>
            itemPayMethods.includes(pm) && defaultAllowedPayMethod.includes(pm)
        )
      }

      if (!availablePaymentMethods.length) {
        break
      }
    }

    // Hasselt feat to be able to mix different payment methods
    if (canMixPaymentMethods) {
      // If there is CC, then we can only use CC, if there is no CC but there is
      // there is invoice then we can only use invoice
      if (allPaymentMethods.has(PayMethodType.PAY_METHOD_CREDIT_CARD)) {
        availablePaymentMethods = [
          PayMethodType.PAY_METHOD_CREDIT_CARD,
          PayMethodType.PAY_METHOD_ACCOUNT
        ]
      } else if (allPaymentMethods.has(PayMethodType.PAY_METHOD_INVOICE)) {
        availablePaymentMethods = [
          PayMethodType.PAY_METHOD_INVOICE,
          PayMethodType.PAY_METHOD_ACCOUNT
        ]
      }
    }

    if (isSalePendingRequired) {
      availablePaymentMethods = availablePaymentMethods.filter(
        (payMethod) => payMethod !== PayMethodType.PAY_METHOD_DIRECT_DEBIT
      )
    }

    return availablePaymentMethods
  }

  private startStoredPayMethodSetupPolling(saleId: number): void {
    this.storedPayMethodSetupPollingSubscription?.unsubscribe()
    this.storedPayMethodSetupPollingSubscription = interval(1000)
      .pipe(untilDestroyed(this), take(10))
      .subscribe(async () => {
        if (this.isLoading) {
          return
        }

        await this.saleFetchAndSideEffects(saleId, this.member)
        if (!this.giftCardRequiresStoredPayMethodSetup()) {
          this.storedPayMethodSetupPollingSubscription?.unsubscribe()
          this.clearStoredPayMethodSetupQueryParam()
        }
      })
  }

  private clearStoredPayMethodSetupQueryParam(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { [storedPayMethodSetupQueryParam]: null },
      queryParamsHandling: 'merge',
      replaceUrl: true
    })
  }

  // this runs every time we have fetches a sale and need to run checkes for side efffects
  private onSaleUpdate(
    sale: Sale,
    payments: Payment[],
    isInitialUpdate?: boolean
  ): void {
    this.sale = sale
    this.payments = payments
    this.saleDocuments = sale?.documents?.length ? sale.documents : []

    if (sale?.statusCode === StatusCode.DONE) {
      if (
        !this.saleDocuments.length &&
        this.showDownloadTicketsButton &&
        this.activeTicketingBlockModule
      ) {
        this.startSaleDocumentsPolling(sale)
      }

      console.log('Sale already completed and will empty cart and other logic')

      this.store.dispatch(
        answerActions.submitPendingSaleSurveyAnswers({ saleId: sale.id })
      )

      this.salePollingSubscription?.unsubscribe()
      this.store.dispatch(cartItemActions.emptyCart())
      this.store.dispatch(cartItemActions.emptyUnAuthenticatedCarItems())

      this.store.dispatch(
        // update store for compatibility with things like pending sale guard, and child components
        normalizationActions.upsertMany({
          entities: normalizeToSchema({ sales: [sale] })
        })
      )

      if (this.member?.id) {
        this.store.dispatch(
          subscriberActions.fetchManyByMemberId({
            memberId: this.member.id,
            force: true
          })
        )
      }

      if (isInitialUpdate) {
        console.log('Sale did initially load as done will not redirect')
      } else {
        this.router.navigate([AppRoutesNS.Sales.SALES_CALLBACK], {
          queryParams: {
            status: 'success',
            paymentReference: payments[0]?.reference
          }
        })
      }
      return
    } else if (
      [StatusCode.INFLIGHT, StatusCode.PENDING_REQUIRED].includes(
        sale?.statusCode
      )
    ) {
      console.log('Sale is pending, checking for payments with status Paid')

      this.amountAlreadyPaid = payments
        .filter((p) => p.saleId === sale.id)
        .filter(
          (payment) =>
            payment?.collectStatus ==
              CollecStatusType.COLLECT_STATUS_COLLECTED ||
            payment.payMethod == PayMethodType.PAY_METHOD_INVOICE
        )
        .reduce((partialSum: number, payment: Payment) => {
          return partialSum + Number(payment.amount)
        }, 0)

      this.dueLaterAmount = payments
        ?.filter(
          (p) => ![PayMethodType.PAY_METHOD_AGGREGATE].includes(p.payMethod)
        )
        .filter((p) => p.saleId === sale.id)
        .filter(
          (payment) =>
            payment?.dueDate && moment(payment.dueDate).isAfter(moment())
        )
        .reduce((partialSum: number, payment: Payment) => {
          return partialSum + Number(payment.amount)
        }, 0)

      this.dueNowAmount =
        Number(sale.total) - this.amountAlreadyPaid - this.dueLaterAmount
      this.checkoutStage = 'unstarted'
    } else if (sale?.statusCode === StatusCode.CANCELLED) {
      this.notificationService.emit('saleModule.saleExpired', 'warning')
      console.log('Sale has been cancelled and will run side effects')
      this.onCancelSideEffects(sale)

      combineLatest([
        this.store.select(cartItemSelectors.selectPartials),
        this.store.select(cartItemSelectors.selectEntities)
      ])
        .pipe(take(1), untilDestroyed(this))
        .subscribe(([cartItemPartials, cartItemEntities]) => {
          if (
            Object.values(cartItemEntities).length > 0 ||
            cartItemPartials.length > 0
          ) {
            this.modalService.show(SaleFailedModalComponent, {
              initialState: {
                sale
              },
              class: 'modal-lg'
            })
          }
        })
    }
  }

  private getSaleExpiresInSeconds(sale: Sale, payments: Payment[]): number {
    if (!sale?.id) {
      return defaultSaleExpirationSeconds // 45 min is the default
    }

    const collectableCardPayments = payments
      .filter((payment) => payment?.id && payment.saleId == sale.id)
      ?.filter(
        (p) =>
          [CollecStatusType.COLLECT_STATUS_COLLECTABLE].includes(
            p.collectStatus
          ) && p.payMethod == PayMethodType.PAY_METHOD_CREDIT_CARD
      )
      ?.filter(
        (payment) =>
          moment().diff(payment.date, 'seconds') < defaultSaleExpirationSeconds
      )
      .sort((a, b) => (a.date < b.date ? 1 : -1))

    if (collectableCardPayments?.length) {
      return Math.max(
        0,
        moment(collectableCardPayments[0].date)
          .clone()
          .add(45, 'minutes')
          .diff(moment(), 'seconds')
      )
    } else {
      return Math.max(
        0,
        moment(sale.date).clone().add(45, 'minutes').diff(moment(), 'seconds')
      )
    }
  }

  private startSaleStatusPolling(saleId: number, member: Member) {
    this.salePollingSubscription = interval(5000)
      .pipe(untilDestroyed(this), startWith(0))
      .subscribe((intervalCount) => {
        if (intervalCount > maxSalePollingCount) {
          this.salePollingSubscription.unsubscribe()
          return
        }

        this.saleFetchAndSideEffects(saleId, member)
      })
  }

  private startSaleDocumentsPolling(sale: Sale): void {
    this.saleDocumentsPollingSubscription?.unsubscribe()

    this.saleService
      .isTicketSale$(sale)
      .pipe(
        filter((isTicketSale) => isTicketSale),
        take(1),
        untilDestroyed(this)
      )
      .subscribe(() => {
        this.saleDocumentsPollingSubscription = interval(1000)
          .pipe(startWith(0), untilDestroyed(this))
          .subscribe(async () => {
            const fetchedSale = await firstValueFrom(
              this.saleService.getOne(
                sale.id,
                RequestQueryBuilder.create({
                  search: {
                    id: sale.id,
                    customerPersonId: this.member.id
                  },
                  join: [{ field: 'documents' }]
                })
              )
            )

            if (fetchedSale.documents?.length) {
              this.saleDocuments = fetchedSale.documents
              this.saleDocumentsPollingSubscription.unsubscribe()
            }
          })
      })
  }

  private validatePaymentMethod(): boolean {
    if (this.selectedStoredPayMethodId) {
      return true
    }
    if (
      this.selectedPaymentMethod === null ||
      this.selectedPaymentMethod === undefined
    ) {
      this.notificationService.emit('alerts.selectAPaymentMethod', 'warning')
      return false
    }
    return true
  }

  private createLegacyDirectDebitStoredPayMethod() {
    const bsModalRef = this.modalService.show(
      CreateStoredPayMethodModalComponent,
      {
        class: 'modal-lg',
        initialState: {
          paymentProviderType: this.selectedPaymentProviderType,
          legacyDmsDirectDebitActive: true
        }
      }
    )

    bsModalRef.onHide?.pipe(untilDestroyed(this), take(1)).subscribe(() => {
      this.saleFetchAndSideEffects(this.sale.id, this.member)
    })
  }

  private getNewPayMethodOrPayOnlineTranslationString(): string {
    return StoredPayMethodCapablePsps.includes(this.selectedPaymentProviderType)
      ? 'storedPayMethodModule.newPayMethod'
      : 'general.pay'
  }

  private onCancelSideEffects(sale: Sale): void {
    this.sale = sale
    this.dueNowAmount = 0
    this.dueLaterAmount = 0
    this.amountAlreadyPaid = 0

    this.store.dispatch(cartItemActions.emptyCart())
    this.store.dispatch(authActions.logoutIfGuest({ redirectToShop: true }))
    this.salePollingSubscription?.unsubscribe()

    this.store.dispatch(bookingActions.resetState()) // delete bookings created for sale as they will be deleted in backbone on calcel if slot generated
    this.store.dispatch(participationActions.resetState()) // delete bookings created for sale as they will be deleted in backbone on calcel if slot generated
    this.store.dispatch(cartItemActions.emptyUnAuthenticatedCarItems())

    this.store.dispatch(
      // update store for compatibility with things ling pending sale guard
      normalizationActions.upsertMany({
        entities: normalizeToSchema({ sales: [sale] })
      })
    )

    this.isLoading = false
  }
}
